'use client';
import React, { useRef, useEffect } from 'react';
import Image from 'next/image';

const showcaseItems = [
  {
    id: 1,
    label: 'Cinematic UGC Scene',
    sublabel: 'Cinematic · YouTube',
    src: '/assets/images/file_00000000f1b07207acb752da697142e5-1778492064748.png',
    alt: 'Cinematic UGC scene with dramatic lighting and product showcase',
    size: 'row-span-2',
  },
  {
    id: 2,
    label: 'Fashion Editorial',
    sublabel: 'Fashion · Pinterest',
    src: '/assets/images/file_00000000a920720b8f5ac48347e4dc76-1778492064000.png',
    alt: 'Fashion editorial style AI-generated creator content',
    size: '',
  },
  {
    id: 3,
    label: 'Fashion Editorial',
    sublabel: 'Fashion · Instagram',
    src: '/assets/images/file_000000005974720b8f91b611bc6c3377-1778492064107.png',
    alt: 'Fashion editorial AI-generated influencer style visual',
    size: '',
  },
  {
    id: 4,
    label: 'AI Creator Selfie',
    sublabel: 'Lifestyle · TikTok',
    src: '/assets/images/file_00000000837c7208b90c307d93fec7c6-1778492064084.png',
    alt: 'AI creator selfie style ultra-realistic influencer content',
    size: 'row-span-2',
  },
  {
    id: 5,
    label: 'Podcast Style',
    sublabel: 'Branded · YouTube',
    src: '/assets/images/1777881978063-1778492063958.png',
    alt: 'Podcast style AI-generated creator content for branded campaigns',
    size: '',
  },
  {
    id: 6,
    label: 'TikTok Style UGC',
    sublabel: 'UGC · TikTok',
    src: '/assets/images/1777572786491-1778493837654.png',
    alt: 'TikTok style UGC AI-generated creator content',
    size: '',
  },
  {
    id: 7,
    label: 'Brand Ad',
    sublabel: 'Brand · Instagram',
    src: '/assets/images/20260511_033126-1778493859955.jpg',
    alt: 'Brand ad AI-generated premium campaign visual',
    size: '',
  },
  {
    id: 8,
    label: 'Brand Ad',
    sublabel: 'Brand · Meta',
    src: '/assets/images/20260511_033232-1778493860440.jpg',
    alt: 'Brand ad AI-generated social media campaign',
    size: '',
  },
];

export default function ShowcaseSection() {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('showcase-active');
          }
        });
      },
      { threshold: 0.1 }
    );
    cardRefs.current.forEach((card) => { if (card) observer.observe(card); });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="showcase" className="relative bg-[#060609] py-28 px-6 md:px-12 overflow-hidden">
      {/* Ambient */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-64 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.08) 0%, transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-pink-500/20 bg-pink-500/5 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-pink-400" />
            <span className="font-sans text-xs text-pink-300 tracking-wide">Visual Showcase</span>
          </div>
          <h2 className="font-sans font-bold text-4xl md:text-5xl text-white leading-tight mb-4">
            Content that looks{' '}
            <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
              impossibly real.
            </span>
          </h2>
          <p className="font-sans text-lg text-white/40">
            Every visual generated with AI — no cameras, no crew, no budget.
          </p>
        </div>

        {/* Editorial grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 auto-rows-[200px]">
          {showcaseItems.map((item, i) => (
            <div
              key={item.id}
              ref={(el) => { cardRefs.current[i] = el; }}
              className={`showcase-card group relative rounded-2xl overflow-hidden cursor-pointer ${item.size}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              {/* Real image */}
              <Image
                src={item.src}
                alt={item.alt}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 50vw, 33vw"
              />

              {/* Dark overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

              {/* Content overlay */}
              <div className="absolute inset-0 flex flex-col justify-between p-4 md:p-5">
                {/* Top badge */}
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/10">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    <span className="font-sans text-[10px] text-white/70 tracking-wide">AI Generated</span>
                  </div>
                </div>

                {/* Bottom info */}
                <div className="translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                  <div className="font-sans text-[10px] text-white/50 tracking-widest uppercase mb-1">{item.sublabel}</div>
                  <div className="font-sans font-semibold text-sm text-white">{item.label}</div>
                </div>
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <p className="text-center font-sans text-sm text-white/25 mt-8">
          All visuals above are AI-generated examples of what&apos;s possible with AI UGC Studio™
        </p>
      </div>

      <style jsx>{`
        .showcase-card {
          opacity: 0;
          transform: scale(0.95) translateY(20px);
          transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .showcase-active {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      `}</style>
    </section>
  );
}
